import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import TopBar from "../../components/TopBar";
import MatrixBackground from "../../components/MatrixBackground";
import { UsersPanel, ShopsPanel, CategoriesPanel, SettingsPanel, AssignRider } from "../../components/admin/AdminPanels";
import { Package, Store, Users, Bike, TrendingUp, IndianRupee } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";

const STATUS_LABEL = {
  placed: "Placed", confirmed: "Confirmed", ready: "Ready", picked_up: "Picked up",
  on_the_way: "Out for delivery", delivered: "Delivered", rejected: "Rejected", cancelled: "Cancelled", awaiting_payment: "Awaiting payment",
};
const STATUS_TONE = {
  placed: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  confirmed: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  ready: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
  picked_up: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  on_the_way: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  delivered: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rejected: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("overview"); // overview | orders | users | shops

  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, o, u, sh, c, st] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/admin/users"),
        api.get("/admin/shops"),
        api.get("/categories"),
        api.get("/settings"),
      ]);
      setStats(s.data);
      setOrders(o.data);
      setUsers(u.data);
      setShops(sh.data);
      setCategories(c.data);
      setSettings((prev) => prev || st.data);
    } catch (e) {
      console.error("Admin load failed:", e);
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [load]);

  if (!stats) {
    return (
      <div className="min-h-screen grid-bg isolate">
        <MatrixBackground opacity={0.2} />
        <TopBar />
        <div className="max-w-6xl mx-auto p-6 text-slate-400">Loading analytics…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid-bg isolate pb-20" data-testid="admin-dashboard">
      <MatrixBackground opacity={0.2} />
      <TopBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-300 font-bold">Admin Control</div>
            <h1 className="heading-font text-3xl font-extrabold">Mission control</h1>
            <div className="text-slate-400 text-sm">Live operations across the stack.</div>
          </div>
          <div className="inline-flex items-center gap-2 glass rounded-full p-1" data-testid="admin-tabs">
            {["overview", "orders", "users", "shops", "categories", "settings"].map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`px-4 py-1.5 rounded-full text-sm font-semibold capitalize ${tab === t ? "btn-neon" : "text-slate-300 hover:text-white"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {tab === "overview" && (
          <div className="mt-6 space-y-6" data-testid="admin-analytics-grid">
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
              <Metric icon={Package} label="Orders" value={stats.total_orders} />
              <Metric icon={IndianRupee} label="Revenue" value={`₹${stats.revenue.toFixed(0)}`} />
              <Metric icon={TrendingUp} label="Delivered" value={stats.delivered} />
              <Metric icon={TrendingUp} label="Live" value={stats.in_progress} />
              <Metric icon={Store} label="Shops" value={stats.total_shops} />
              <Metric icon={Bike} label="Riders" value={stats.total_riders} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="glass rounded-3xl p-5">
                <div className="heading-font text-sm font-bold uppercase tracking-widest text-slate-400">Orders / day</div>
                <div className="h-60 mt-3">
                  <ResponsiveContainer>
                    <AreaChart data={stats.orders_per_day}>
                      <defs>
                        <linearGradient id="ordersFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                      <XAxis dataKey="date" stroke="#64748B" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                      <YAxis stroke="#64748B" fontSize={10} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "#0A0E17", border: "1px solid rgba(0,240,255,0.2)", borderRadius: 12, color: "#F8FAFC" }} />
                      <Area type="monotone" dataKey="orders" stroke="#00F0FF" strokeWidth={2.5} fill="url(#ordersFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="glass rounded-3xl p-5">
                <div className="heading-font text-sm font-bold uppercase tracking-widest text-slate-400">Top categories</div>
                <div className="h-60 mt-3">
                  <ResponsiveContainer>
                    <BarChart data={stats.top_categories}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                      <XAxis dataKey="category" stroke="#64748B" fontSize={10} />
                      <YAxis stroke="#64748B" fontSize={10} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "#0A0E17", border: "1px solid rgba(0,240,255,0.2)", borderRadius: 12, color: "#F8FAFC" }} />
                      <Bar dataKey="orders" fill="#0066FF" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="glass rounded-3xl p-5">
              <div className="heading-font text-sm font-bold uppercase tracking-widest text-slate-400">Top shops</div>
              <div className="mt-3 divide-y divide-cyan-500/10">
                {stats.top_shops.length === 0 && <div className="text-slate-400 text-sm py-4">No orders yet.</div>}
                {stats.top_shops.map((s, i) => (
                  <div key={s.shop_id} className="py-3 flex items-center justify-between" data-testid={`top-shop-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-cyan-400/10 border border-cyan-500/25 inline-flex items-center justify-center text-cyan-300 font-bold text-xs">
                        {i + 1}
                      </div>
                      <div className="font-semibold">{s.shop_name}</div>
                    </div>
                    <div className="text-sm flex items-center gap-4">
                      <span className="text-slate-400">{s.orders} orders</span>
                      <span className="font-bold heading-font">₹{s.revenue}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-6 glass rounded-3xl overflow-hidden">
            <div className="p-4 border-b border-cyan-500/15 heading-font font-bold">All orders ({orders.length})</div>
            <div className="divide-y divide-cyan-500/10 max-h-[70vh] overflow-y-auto">
              {orders.map((o) => (
                <div key={o.order_id} className="p-4 flex items-center justify-between gap-4" data-testid={`admin-order-${o.order_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="mono text-xs text-slate-500">#{o.order_id.slice(-6).toUpperCase()}</div>
                      <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full border ${STATUS_TONE[o.status]}`}>{STATUS_LABEL[o.status]}</span>
                    </div>
                    <div className="text-sm font-semibold truncate">{o.customer_name} → {o.shop_name}</div>
                    <div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()} · {o.items.length} item{o.items.length > 1 ? "s" : ""} · {o.rider_name ? `Rider: ${o.rider_name}` : "No rider"}</div>
                  </div>
                  <AssignRider order={o} riders={users.filter((u) => u.role === "rider")} reload={load} />
                  <div className="heading-font font-extrabold">₹{o.total.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "users" && <UsersPanel users={users} reload={load} />}

        {tab === "shops" && <ShopsPanel shops={shops} reload={load} />}

        {tab === "categories" && <CategoriesPanel categories={categories} reload={load} />}

        {tab === "settings" && settings && <SettingsPanel settings={settings} reload={load} />}
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="glass rounded-2xl p-4 relative overflow-hidden">
      <div className="absolute -right-6 -top-6 w-20 h-20 bg-cyan-400/10 blur-2xl rounded-full" />
      <div className="relative flex items-center gap-2 text-cyan-300">
        <Icon size={14} />
        <div className="text-[10px] uppercase tracking-widest font-bold">{label}</div>
      </div>
      <div className="relative heading-font text-2xl font-extrabold mt-1">{value}</div>
    </div>
  );
}
